import React from "react";
import {
  createFileRoute,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import {
  Box,
  BreadcrumbItem,
  Separator,
  Spinner,
  Tabs,
} from "@salestrak/ui";

import {
  SortableTab,
  SortableTabsList,
  TabsPillIcon,
  type TabsPillIconProps,
} from "@salestrak/ui";
import { PageHeader, PageHeaderBreadcrumb } from "#app/components/page-header";
import { EntityBreadcrumbLink } from "#app/components/entity";
import { AccessDenied } from "#app/components/permissions/require-permission";
import { issueFilterConfig } from "#app/lib/filters/configs/issue-filter-config";
import { FilterPanel } from "#app/components/filters/filter-panel";
import {
  IssueFilterProvider,
  useIssueFilters,
} from "#app/lib/filters/issue-filter-context";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { teamRefToEntity } from "#app/lib/entity-visuals";
import { useWidgetsFlag } from "#app/lib/hooks/use-widgets-flag";
import {
  useTeamViewTabOrder,
  type TeamViewTab,
} from "#app/lib/hooks/use-view-tab-order";
import { useAnalytics, AnalyticsContext, IssueEvent, AnalyticsScope } from "#app/lib/analytics";

/**
 * Single-team chrome wrapping the view routes (`/board`, `/list`, and the
 * WIDGETS-gated `/overview` — the flag-on landing tab — and `/dashboard`): the
 * PageHeader breadcrumb masthead, the view tabs, an object-level access guard,
 * and the shared filter context + filter-panel overlay. The header renders
 * here once, above `<Outlet />`, so it's identical across all views; each
 * route renders only its own body and toolbar.
 */

// Board/List share their chip colors with the workflow view tabs (one visual
// language for the same view concept); Overview takes teal so no two chips in
// this row clash.
const TAB_META: Record<
  TeamViewTab,
  { icon: TabsPillIconProps["icon"]; color: TabsPillIconProps["color"]; label: string }
> = {
  overview: { icon: "home", color: "teal", label: "Overview" },
  board: { icon: "columns-3", color: "light-blue", label: "Board" },
  list: { icon: "list", color: "violet", label: "List" },
  dashboard: { icon: "chart-column-increasing", color: "orange", label: "Dashboard" },
};

function useActiveTab(): TeamViewTab {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lastSegment = pathname.split("/").pop();
  if (lastSegment === "board") return "board";
  if (lastSegment === "list") return "list";
  if (lastSegment === "dashboard") return "dashboard";
  return "overview";
}

function TeamLayout() {
  const { teamId } = Route.useParams();
  return (
    <IssueFilterProvider key={teamId}>
      <TeamLayoutInner />
    </IssueFilterProvider>
  );
}

function TeamLayoutInner() {
  const { teamId } = Route.useParams();
  const { replace } = useAppNavigate();
  const { track } = useAnalytics();
  const { teamMap, isHydrated } = useWorkspaceData();
  const { filters, filterPanelOpen, closeFilterPanel } = useIssueFilters();

  const team = teamId ? teamMap.get(teamId) : undefined;

  const activeTab = useActiveTab();
  const widgetsEnabled = useWidgetsFlag();
  const { order: tabOrder, reorderVisible } = useTeamViewTabOrder();

  // Overview and Dashboard are WIDGETS-gated; the saved order keeps their
  // slots so flag flips restore them in place.
  const visibleTabs = React.useMemo(
    () =>
      widgetsEnabled
        ? tabOrder
        : tabOrder.filter((tab) => tab === "board" || tab === "list"),
    [tabOrder, widgetsEnabled],
  );

  const navigateTab = React.useCallback(
    (tab: string) => {
      if (tab === activeTab) return;
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.SwitchTab,
        data: { scope: AnalyticsScope.Team, tab },
      });
      replace(`/team/${teamId}/${tab}`);
    },
    [activeTab, teamId, replace, track],
  );

  // Object-level guard: teams are filtered by visibility, so a team missing
  // from the loaded workspace data means the viewer can't see it (or it doesn't
  // exist). Wait for hydration before deciding so a fresh load isn't denied.
  if (!isHydrated) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (!team) {
    return <AccessDenied />;
  }

  return (
    <Box fill="flex" direction="column">
      <PageHeader>
        <PageHeaderBreadcrumb>
          <BreadcrumbItem>
            <EntityBreadcrumbLink
              onClick={() => navigateTab(widgetsEnabled ? "overview" : "board")}
              entity={teamRefToEntity(team)}
              active
            />
          </BreadcrumbItem>
        </PageHeaderBreadcrumb>
      </PageHeader>

      <Box paddingX="md">
        <Tabs value={activeTab} onValueChange={navigateTab}>
          <SortableTabsList order={visibleTabs} onOrderChange={reorderVisible}>
            {visibleTabs.map((tab) => (
              <SortableTab key={tab} value={tab}>
                <TabsPillIcon
                  icon={TAB_META[tab].icon}
                  color={TAB_META[tab].color}
                />
                {TAB_META[tab].label}
              </SortableTab>
            ))}
          </SortableTabsList>
        </Tabs>
      </Box>

      <Separator />
      <Box fill="flex" direction="column">
        <Outlet />
      </Box>

      <FilterPanel
        open={filterPanelOpen}
        onOpenChange={closeFilterPanel}
        config={issueFilterConfig}
        filters={filters}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/team/$teamId")({
  component: TeamLayout,
});
